// import type { MapItem } from '4-sections/AssetsListing/Map/ListingMap';
import { getIconByCategory } from '3-components/ListingMap/ListingMapHelpers';
import type { AssetMapItem, LocationMapItem, RealEstateMapItem } from '3-components/ListingMap/MapTypes';
import { type AssetsListingQuery, type LocationListingQuery, type RealEstateListingQuery } from 'cms/cms';
import type { ContentModelReference, ContentModelReferenceSearch, Country } from 'cms/cms-types';
import { resolveImageReferenceToImage } from './imageResolver';
import { generateDescription, type LocationCardResolverSettings } from './locationCardResolver';
import { resolveCategoriesToMapCategory } from './mapCategoryResolver';

export type AssetPage = NonNullable<AssetsListingQuery['AssetPage']>;
export type AssetPageItems = NonNullable<AssetPage['items']>;

export const resolveAssetPagesToMapItems = (items: AssetPageItems): AssetMapItem[] => {
    return items.map(item => {
        // // the autogenerated produce | null for each item, need to remove the | null type
        const {
            ContentLink,
            Title,
            PreviewTitle,
            PreviewDescription,
            PreviewImage,
            AssetTypeCategories,
            SectorCategories,
            Latitude,
            Longitude,
            CountryItems,
            // Countries
        } = item as NonNullable<typeof item>;
        const id = String((ContentLink as NonNullable<typeof ContentLink>).Id);
        const imgSrcSets = [480, 767];
        const img = resolveImageReferenceToImage(PreviewImage as ContentModelReference, '?width=340', imgSrcSets);

        const assetType = resolveCategoriesToMapCategory(AssetTypeCategories)?.description || '';

        // for icon
        const assetTypeName = resolveCategoriesToMapCategory(AssetTypeCategories)?.name || '';

        const sector = resolveCategoriesToMapCategory(SectorCategories)?.description || '';

        const coordinates = {
            lat: Number(Latitude),
            lon: Number(Longitude),
        };

        const countries = CountryItems?.map(countryItem => {
            const { Name, PlaceId } = (((countryItem as NonNullable<typeof countryItem>).ContentLink as ContentModelReferenceSearch).Expanded as Country);
            return {
                name: Name || '',
                placeId: PlaceId || '',
            };
        }) || [];

        return {
            id,
            name: PreviewTitle || Title as string,
            description: PreviewDescription || '',
            ...(img && { img }),
            assetType,
            assetTypeName,
            sector,
            coordinates,
            countries,
            href: ContentLink?.Url ? ContentLink.Url : '',
            icon: getIconByCategory(assetTypeName),
        };
    });
};

export type LocationPage = NonNullable<LocationListingQuery['LocationPage']>;
export type LocationPageItems = NonNullable<LocationPage['items']>;

export const resolveLocationPagesToMapItems = (items: LocationPageItems, settings: LocationCardResolverSettings): LocationMapItem[] => {
    return items.map(item => {
        // // the autogenerated produce | null for each item, need to remove the | null type
        const {
            ContentLink,
            Title,
            PreviewTitle,
            Address,
            PhoneNumber,
            FaxNumber,
            PreviewImage,
            Latitude,
            Longitude,
            // Countries
        } = item as NonNullable<typeof item>;
        const { faxLabel, telLabel } = settings;
        const id = String((ContentLink as NonNullable<typeof ContentLink>).Id);
        const imgSrcSets = [480, 767];
        const img = resolveImageReferenceToImage(PreviewImage as ContentModelReference, '?width=340', imgSrcSets);

        const coordinates = {
            lat: Number(Latitude),
            lon: Number(Longitude),
        };

        const description = generateDescription({ Address, FaxNumber, PhoneNumber, faxLabel, telLabel });

        return {
            id,
            name: PreviewTitle || Title as string,
            description,
            ...(img && { img }),
            coordinates,
            href: ContentLink?.Url ? ContentLink.Url : '',
            icon: 'map',
        };
    });
};

export type RealEstatePage = NonNullable<RealEstateListingQuery['RealestatePage']>;
export type RealEstatePageItems = NonNullable<RealEstatePage['items']>;

export const resolveRealEstatePagesToMapItems = (items: RealEstatePageItems): RealEstateMapItem[] => {
    return items.map(item => {
        // // the autogenerated produce | null for each item, need to remove the | null type
        const {
            ContentLink,
            AssetName,
            Excerpt,
            PreviewTitle,
            PreviewDescription,
            PreviewImage,
            SectorCategories,
            StateCategories,
            GradeCategories,
            StatusCategories,
            Latitude,
            Longitude,
        } = item as NonNullable<typeof item>;
        const id = String((ContentLink as NonNullable<typeof ContentLink>).Id);
        const imgSrcSets = [480, 767];
        const img = resolveImageReferenceToImage(PreviewImage as ContentModelReference, '?width=340', imgSrcSets);

        const sector = resolveCategoriesToMapCategory(SectorCategories)?.description || '';
        const state = resolveCategoriesToMapCategory(StateCategories)?.description || '';
        const grade = resolveCategoriesToMapCategory(GradeCategories)?.description || '';
        const status = resolveCategoriesToMapCategory(StatusCategories)?.description || '';

        const coordinates = {
            lat: Number(Latitude),
            lon: Number(Longitude),
        };

        return {
            id,
            name: PreviewTitle ?? AssetName ?? '',
            description: PreviewDescription ?? Excerpt ?? '',
            ...(img && { img }),
            sector,
            state,
            grade,
            status,
            coordinates,
            href: ContentLink?.Url ? ContentLink.Url : '',
            icon: getIconByCategory(resolveCategoriesToMapCategory(SectorCategories)?.name || ''),
        };
    });
};
